import autoTable from "jspdf-autotable";
import { taxDoc } from "./reports";
import { pdfToImages } from "./pdfExtract";
import { fmt, today } from "./format";

const TEAL = [13, 148, 136];
const SECTIONS = [["business", "Business"], ["charity", "Charity"], ["medical", "Medical"]];

export async function imageToJpeg(blob) {
  const bmp = await createImageBitmap(blob);
  const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * scale);
  c.height = Math.round(bmp.height * scale);
  const g = c.getContext("2d");
  g.fillStyle = "#fff";
  g.fillRect(0, 0, c.width, c.height);
  g.drawImage(bmp, 0, 0, c.width, c.height);
  return [{ url: c.toDataURL("image/jpeg", 0.82), w: c.width, h: c.height }];
}

// Every receipt attached to a tax-pack row for the year, in date order
export function packReceipts(state, t) {
  const seen = new Set();
  const byId = Object.fromEntries(state.transactions.map((x) => [x.id, x]));
  return SECTIONS.flatMap(([k, label]) => t[k].rows.map((r) => ({ r, label })))
    .filter(({ r }) => !seen.has(r.id) && seen.add(r.id))
    .flatMap(({ r, label }) => (byId[r.id]?.receipts || []).map((meta) => ({ meta, row: r, section: label })))
    .sort((a, b) => a.row.date.localeCompare(b.row.date));
}

// Tax pack + mileage log + receipt index + one page per receipt photo, as a single PDF
export async function buildAccountantPack(state, year, getReceipt, onProgress) {
  const { doc, t } = taxDoc(state, year, "Accountant Pack");
  const list = packReceipts(state, t);
  doc.addPage();
  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(...TEAL).text(`Receipt index — ${list.length} receipt(s)`, 40, 50);
  doc.setTextColor(20);
  autoTable(doc, { startY: 60, head: [["#", "Date", "Section", "Description", "Amount", "File"]], body: list.length ? list.map((x, i) => [i + 1, x.row.date, x.section, x.row.description, fmt(x.row.amount), x.meta.name]) : [["", "", "", "No receipts attached for this year", "", ""]], margin: { left: 40, right: 40 }, styles: { fontSize: 8 }, headStyles: { fillColor: TEAL } });
  let missing = 0;
  for (let i = 0; i < list.length; i++) {
    const { meta, row, section } = list[i];
    onProgress?.(i + 1, list.length);
    let pages = [];
    try {
      const blob = await getReceipt(meta);
      if (blob) pages = meta.type === "application/pdf" ? await pdfToImages(await blob.arrayBuffer()) : await imageToJpeg(blob);
    } catch (e) {
      console.error(e);
    }
    if (!pages.length) missing++;
    (pages.length ? pages : [null]).forEach((img, p) => {
      doc.addPage();
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(...TEAL).text(`Receipt #${i + 1}${pages.length > 1 ? ` (page ${p + 1})` : ""}`, 40, 44);
      doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(60).text(`${row.date} · ${section} · ${row.description} · ${fmt(row.amount)}${row.notes ? ` · ${row.notes}` : ""}`.slice(0, 130), 40, 60);
      doc.setTextColor(20);
      if (!img) return doc.setFontSize(11).text("This receipt's photo isn't stored on the device that made this pack.", 40, 100);
      const maxW = W - 80;
      const maxH = H - 110;
      const s = Math.min(maxW / img.w, maxH / img.h);
      doc.addImage(img.url, "JPEG", 40 + (maxW - img.w * s) / 2, 76, img.w * s, img.h * s);
    });
  }
  const n = doc.getNumberOfPages();
  for (let p = 1; p <= n; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(130).text(`Accountant pack ${year} · prepared ${today()} · page ${p} of ${n}`, 40, doc.internal.pageSize.getHeight() - 20);
  }
  return { blob: doc.output("blob"), count: list.length, missing, pages: n };
}
